import React, {useEffect, useRef, useState} from "react";
import Switch from "react-bootstrap-switch";
import {
    Button, Card, CardBody, CardTitle, Col,
    Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row
} from "reactstrap";
import { ErrorMessage, useForm } from "react-hook-form";
import {connect, useSelector} from "react-redux";
import * as Yup from "yup";
import {getStateByPath} from "../../../redux/selectors";



// components
import ActivityLangs from "./_langs";
import Separator from "../../../components/Separator";
import SelectGeneric from "../../../components/select/selectGeneric";
import {MapPickerModal} from "../../../components/map/MapPickerModal";
import {showToastCustom} from "../../../components/toast/customToast";
import {GenericButton} from "../../../components/buttons/customButtons";
import TranslateButton from "../../../components/buttons/TranslateButton";
import ActivityItinerarySection from "./ItinerarySection/ItinerarySection";
import ImagePicker from "../../../components/imagePickerComponents/ImagePicker";
import ActivityScheduleSection from "./ScheduleSection/ActivityScheduleSection";
import { LogHistoryModal } from "../../../components/logHistory/logHistoryModal";
import SelectMultipleGeneric from "../../../components/select/selectMultipleGeneric";

// labels
import {TT_CLOSE, TT_HISTORY, TT_SAVE} from "../../../components/i18n/toolTips";
import {error, INTERNATIONAL, selectOption,} from "../../../components/i18n/i18n";
import {
    FL_ADITIONAL_COST, FL_CODE,
    FL_DESTINATION, FL_IMAGE, FL_NUMBER, FL_ORDER,
    FL_STATUS, FL_TYPE_DESTINATION_ACTIVITY
} from "../../../components/i18n/formLabels";
import {PH_CODE, PH_IMAGE, PH_ORDER} from "../../../components/i18n/placeHolders";

// actions
import {genericIndexedActions} from "../../../redux/actions/generics/GenericIndexedActions";
import {setForm, setItem, setLoading, setViewMode} from "../../../redux/actions/generics/IndexedActions";

// utils
import {getCurrentDateForCodeField} from "../../../util/DateTools";

// services & providers
import { tagProvider } from "../../../providers/tagProvider";
import { routProvider } from "../../../providers/routProvider";
import {eventService} from "../../../jwt/_services/core/eventService";
import { categoryProvider } from "../../../providers/categoryProvider";
import { destinationProvider } from "../../../providers/destinationProvider";
import { restrictionProvider } from "../../../providers/restrictionProvider";
import { activityService } from "../../../jwt/_services/core/activityService";
import {activityMediaService} from "../../../jwt/_services/core/activityMediaService";
import { typeAudienceProvider } from "../../../providers/eventx/typeAudienceProvider";
import { typeDestinationActivityProvider } from "../../../providers/typeDestinationActivityProvider";
import {setEditMode} from "../../../redux/actions/genericActions";

/**
 * ActivityForm is a React component that renders a form for creating or editing an activity.
 * It uses various hooks and components from react, reactstrap, react-hook-form, and redux.
 * It also uses various custom components, actions, providers, and services.
 *
 * @param {Object} props - The properties passed to the component.
 */
const ActivityForm = props => {
    // Destructure the properties needed from the props.

    const componentState = useSelector(state => getStateByPath(state, props.path));

    const { path } = componentState;
    const { app, udn, editMode, domainObject, mapConfig} = componentState?.data;

    // Define state variables using the useState hook.
    // The initial state is derived from the domainObject passed in the props.
    const [status, setStatus] = useState( domainObject?.status !== undefined ? domainObject?.status : true);
    const [aditionalCost, setAditionalCost] = useState(domainObject?.aditionalCost !== undefined ? domainObject?.aditionalCost : null);
    const [highlight, setHighlight] = useState(domainObject?.highlight !== undefined ? domainObject?.highlight : null);
    const [forChildren, setForChildren] = useState(domainObject?.forChildren !== undefined ? domainObject?.forChildren : null );
    const [forHandicapped, setForHandicapped] = useState(domainObject?.forHandicapped !== undefined ? domainObject?.forHandicapped : null );
    const [isNew, setIsNew] = useState(domainObject?.isNew !== undefined ? domainObject?.isNew : null);
    const [saleStatus, setSaleStatus] = useState(domainObject?.saleStatus !== undefined ? domainObject?.saleStatus : null);

    const [routId, setRoutId] = useState(domainObject?.rout?.id || null);
    const [categoryId, setCategoryId] = useState(domainObject?.category?.id || null);
    const [typeAudienceId, setTypeAudienceId] = useState( domainObject?.typeAudience?.id || null);
    const [typeDestinationActivityId, setTypeDestinationActivityId] = useState(domainObject?.typeDestinationActivity?.id || null);
    const [destinationId, setDestinationId] = useState(domainObject?.destination?.id || null);
    const [position, setPosition] = useState(editMode ? {
        lat: parseFloat(domainObject?.latitude),
        lng: parseFloat(domainObject?.longitude)
    } : mapConfig?.center );

    const [langs, setLangs] = useState(domainObject?.langs || []);
    const translatableLangs = domainObject?.translatedLangs || [];
    const [tags, setTags] = useState(domainObject?.tags || []);
    const [restrictions, setRestrictions] = useState(domainObject?.restrictions || []);

    const [showHistoryModal, setShowHistoryModal] = useState(true);
    const [showMapModal, setShowMapModal] = useState(true);
    const [event, setEvent] = useState(domainObject?.event?.id || null);
    const [paxes, setPaxes] = useState(domainObject?.paxes || []);

    // Define refs using the useRef hook.
    const modalHistoryRef = useRef(null);
    const modalMapRef = useRef(null);


    // Actualiza el formulario con los datos del objeto de dominio
    // Se utiliza posterior a la carga de imagenes desde ImagePicker ejecutando el metodo refresh()
    useEffect(() => {
        if (domainObject && domainObject.id && domainObject.langs) {
            reset({
                maxDeepMts: domainObject?.maxDeepMts,
                maxDeepFts: domainObject?.maxDeepFts,
                maxHeigthMts: domainObject?.maxHeigthMts,
                maxHeigthFts: domainObject?.maxHeigthFts,
                minAge: domainObject?.minAge,
                capacity: domainObject?.capacity,
                number: domainObject?.number,
                showOrder: domainObject?.showOrder,
                highlight: domainObject?.highlight,
                score: domainObject?.score,
                videoUrl: domainObject?.videoUrl,
                code: (domainObject?.code || `${udn?.code}_activity_` + getCurrentDateForCodeField()).toLowerCase(), // AUTOCODE
                image: domainObject?.image,
                xapiId: domainObject?.addonActivity?.xapiId,
                xapiCode: domainObject?.addonActivity?.xapiCode,
                xamServiceId: domainObject?.xamServiceId,
                xamLocationId: domainObject?.xamLocationId,
            })
            setLangs(domainObject.langs || []);
        }
    }, [ domainObject ]);

    // Define functions to show modals.
    const showHistory = () => {
        setShowHistoryModal(true);
        modalHistoryRef.current.showModal(domainObject);
    }

    const showMap = () => {
        setShowMapModal(true);
        modalMapRef.current.showModal(mapConfig);
    }

    // Define the validation schema for the form using Yup.
    const validationSchema = Yup.object().shape({
        // code: Yup.string().required(REQUIRED).max(MAX_NAME_SIZE, MAX_NAME),
    });

    // Initialize the form using the useForm hook from react-hook-form.
    const {register, handleSubmit, reset, errors} = useForm({
        validationSchema: validationSchema,
        defaultValues: {
            maxDeepMts: domainObject?.maxDeepMts,
            maxDeepFts: domainObject?.maxDeepFts,
            maxHeigthMts: domainObject?.maxHeigthMts,
            maxHeigthFts: domainObject?.maxHeigthFts,
            minAge: domainObject?.minAge,
            capacity: domainObject?.capacity,
            number: domainObject?.number,
            showOrder: domainObject?.showOrder,
            highlight: domainObject?.highlight,
            score: domainObject?.score,
            videoUrl: domainObject?.videoUrl,
            code: (domainObject?.code || `${udn?.code}_activity_` + getCurrentDateForCodeField()).toLowerCase(), // AUTOCODE
            image: domainObject?.image,
            xapiId: domainObject?.addonActivity?.xapiId,
            xapiCode: domainObject?.addonActivity?.xapiCode,
            xamServiceId: domainObject?.xamServiceId,
            xamLocationId: domainObject?.xamLocationId,
        }
    });

    // Define a function to validation rules before submitting.
    const validateSubmit = () => {
        switch (app?.code?.toUpperCase()) {
            case "PARK" :
                return categoryId !== null
            case "FERRY" :
                return destinationId !== null && typeDestinationActivityId !== null
            case "EVENTS" :
                return categoryId !== null
            default:
                return true
        }
    }

    /**
     * Handles the form submission.
     *
     * @param {Object} data - The form data.
     * @param {Object} _ - An unused parameter.
     */
    const onSubmit = (data, _) => {
        if (!validateSubmit())
            return showToastCustom(error, selectOption);

        let dataRequest = {
            fbDataBase :  { id : app?.fbDataBase?.id },
            item : {
                ...data,
                id: domainObject?.id,
                udn: {id: udn?.id},

                latitude: position?.lat,
                longitude: position?.lng,

                // Booleans
                aditionalCost: aditionalCost,
                forChildren: forChildren,
                forHandicapped: forHandicapped,
                highlight: highlight,
                isNew: isNew,
                status: status,
                langs: langs,
                saleStatus: saleStatus,
                tags: tags.map( t => t?.id ),
                restrictions: restrictions.map( m => m?.id ),
                category:{id:categoryId}
            }
        };

        if(event)
            dataRequest.item.event = { id: event };

        if (routId)
            dataRequest.item.rout = { id: routId };

        if (typeAudienceId)
            dataRequest.item.typeAudience = { id: typeAudienceId };

        if (destinationId)
            dataRequest.item.destination = { id: destinationId };

        if (typeDestinationActivityId)
            dataRequest.item.typeDestinationActivity = { id: typeDestinationActivityId };

        if (data.xapiId || data.xapiCode) {
            dataRequest.item.addonActivity = {
                id: domainObject?.addonActivity?.id,
                activity: { id: domainObject?.id },
                xapiId : data.xapiId,
                xapiCode : data.xapiCode
            }
        }

        if (editMode) {
            let id = domainObject.id;
            dataRequest.item.id = id

            genericIndexedActions.update(path, activityService, id, dataRequest)
        } else {
            genericIndexedActions.save(path, activityService, dataRequest)
        }

        closeForm()
    };

    // const onCopy = () => {
    //     let dataRequest = {
    //         fbDataBase :  { id : app?.fbDataBase?.id },
    //         item : {
    //             ...domainObject,
    //             id : null,
    //             langs : domainObject?.langs?.map( l => ({
    //                 lang : l.lang,
    //                 id : null,
    //                 description : l.description,
    //                 included : l.included,
    //                 name: l.name,
    //                 warning: l.warning,
    //                 genreDetail: l.genreDetail,
    //                 artist: l.artist,
    //                 sellingUrl: l.sellingUrl,
    //                 callToActionUrl: l.callToActionUrl,
    //                 callToActionLabel: l.callToActionLabel
    //             })),
    //         }
    //     };
    //
    //     console.log("dataRequest", dataRequest);
    //
    //     genericIndexedActions.save(path, activityService, dataRequest)
    //     closeForm()
    // }

    // Define a function to close the form.
    const closeForm = () => {
        reset();
        props.setItem(path, null);
        props.setForm(path, );
        props.onClose();
    }

    // Define functions to handle changes in latitude and longitude.
    const onLatChange = (value ) => {
        setPosition({
            ...position,
            lat : value
        })
    }

    const onLngChange = ( value ) => {
        setPosition({
            ...position,
            lng : value
        })
    }

    const refreshObject = () => {
        genericIndexedActions.get(path, activityService, domainObject?.id, app?.fbDataBase?.id);
    }


    // Define functions to render different forms based on the app code.
    const formAny = () => {
        return <>
            <Row>
                <Col>
                    <Row>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_ORDER}</Label>
                                <Input type="number" name="showOrder"
                                       placeholder="Orden Visualización"
                                       innerRef={register}
                                       className={(errors.showOrder ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_NUMBER}</Label>
                                <Input type="number" name="number"
                                       placeholder="Ingrese numero"
                                       innerRef={register}
                                       className={(errors.number ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Edad minima</Label>
                                <Input type="number" name="minAge"
                                       placeholder="Ingrese edad minima"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Capacidad</Label>
                                <Input type="number" name="capacity"
                                       placeholder="Ingrese capacidad"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Maxima profundidad (metros)</Label>
                                <Input type="maxDeepMts" name="maxDeepMts"
                                       placeholder="Ingrese profundidad (metros)"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Altura maxima (metros)</Label>
                                <Input type="maxHeigthMts" name="maxHeigthMts"
                                       placeholder="Ingrese altura maxima (metros)"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>Ruta</Label>
                                <SelectGeneric
                                    initialValue={routId}
                                    setExternalValue={setRoutId}
                                    provider={routProvider}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Categoria</Label>
                                <SelectGeneric
                                    initialValue={categoryId}
                                    setExternalValue={setCategoryId}
                                    provider={categoryProvider}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Latitud y longitud</Label>
                                <InputGroup>
                                    <Button color=" background-color-primary" onClick={showMap}>
                                        <i className="fas fa-map-marker-alt text-white"/>
                                    </Button>
                                    <InputGroupAddon addonType="append">
                                        <Input type="number"
                                               name="lat"
                                               value={position?.lat}
                                               className={'lat-lng-input'}
                                               step={'0.0000001'}
                                               onChange={e => onLatChange(e.target.value)}
                                        />
                                    </InputGroupAddon>
                                    <InputGroupAddon addonType="append">
                                        <Input type="number"
                                               name="lng"
                                               className={'lat-lng-input'}
                                               value={position?.lng}
                                               step={'0.0000001'}
                                               onChange={e => onLngChange(e.target.value)}
                                        />
                                    </InputGroupAddon>
                                </InputGroup>
                                <ErrorMessage errors={errors} name="latitude" component="div"
                                              className="invalid-feedback"/>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="2">
                            <FormGroup>
                                <Label>Activo</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={status}
                                        innerRef={register}
                                        onChange={_ => setStatus(!status)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Costo adicional</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={aditionalCost}
                                        innerRef={register}
                                        onChange={_ => setAditionalCost(!aditionalCost)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Para niños</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={forChildren}
                                        innerRef={register}
                                        onChange={_ => setForChildren(!forChildren)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Para discapacitados</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={forHandicapped}
                                        innerRef={register}
                                        onChange={_ => setForHandicapped(!forHandicapped)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Nueva actividad</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={isNew}
                                        innerRef={register}
                                        onChange={_ => setIsNew(!isNew)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>

                        <Col md="2">
                            <FormGroup>
                                <Label>Xapi Id</Label>
                                <Input type="number"
                                       name="xapiId"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>Xapi Code</Label>
                                <Input type="text"
                                       name="xapiCode"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <SelectMultipleGeneric
                                    title={'Restricciones'}
                                    initialValues={restrictions}
                                    setExternalValue={setRestrictions}
                                    provider={restrictionProvider}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <SelectMultipleGeneric
                                    title={'Tags'}
                                    initialValues={tags}
                                    setExternalValue={setTags}
                                    provider={tagProvider}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                {getInternational()}
            </Row>
        </>
    }

    const formPark = () => {
        return <>
            <Row>
                <Col md={domainObject?.id !== undefined ? '6' : '12'}>

                    <div className={'form-container'}>
                        <div className={''}>
                            <h2>{'Configuración operativa'}</h2>
                        </div>
                        <div className={''}>
                            <Row>

                                <Col md="3">
                                    <FormGroup>
                                        <Label>Edad minima</Label>
                                        <Input type="number" name="minAge"
                                               placeholder="Ingrese edad minima"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Capacidad</Label>
                                        <Input type="number" name="capacity"
                                               placeholder="Ingrese capacidad"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Maxima profundidad (metros)</Label>
                                        <Input type="maxDeepMts" name="maxDeepMts"
                                               placeholder="Ingrese profundidad (metros)"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Altura maxima (metros)</Label>
                                        <Input type="maxHeigthMts" name="maxHeigthMts"
                                               placeholder="Ingrese altura maxima (metros)"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md="6">
                                    <FormGroup>
                                        <Label>Ruta</Label>
                                        <SelectGeneric
                                            initialValue={routId}
                                            setExternalValue={setRoutId}
                                            provider={routProvider}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Categoria</Label>
                                        <SelectGeneric
                                            initialValue={categoryId}
                                            setExternalValue={setCategoryId}
                                            provider={categoryProvider}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Costo adicional</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={aditionalCost}
                                                innerRef={register}
                                                onChange={_ => setAditionalCost(!aditionalCost)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Para niños</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={forChildren}
                                                innerRef={register}
                                                onChange={_ => setForChildren(!forChildren)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Para discapacitados</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={forHandicapped}
                                                innerRef={register}
                                                onChange={_ => setForHandicapped(!forHandicapped)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Nueva actividad</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={isNew}
                                                innerRef={register}
                                                onChange={_ => setIsNew(!isNew)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <SelectMultipleGeneric
                                            title={'Restricciones'}
                                            initialValues={restrictions}
                                            setExternalValue={setRestrictions}
                                            provider={restrictionProvider}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <SelectMultipleGeneric
                                            title={'Tags'}
                                            initialValues={tags}
                                            setExternalValue={setTags}
                                            provider={tagProvider}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className={'form-container'}>
                        <div className={''}>
                            <h2>{'Configuración de venta'}</h2>
                        </div>
                        <div className={''}>
                            <Row>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>Xapi Id</Label>
                                        <Input type="number"
                                               name="xapiId"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>Xapi Code</Label>
                                        <Input type="text"
                                               name="xapiCode"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>{'XAM Id'}</Label>
                                        <Input type="number"
                                               min={0}
                                               name="xamServiceId"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>{'XAM Locacion Id'}</Label>
                                        <Input type="number"
                                               min={0}
                                               name="xamLocationId"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>{"Habilitar venta"}</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={saleStatus}
                                                innerRef={register}
                                                onChange={_ => setSaleStatus(!saleStatus)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className={'form-container'}>
                        <div className={''}>
                            <h2>{'Datos de presentación'}</h2>
                        </div>
                        <div className={''}>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>{FL_ORDER}</Label>
                                        <Input type="number" name="showOrder"
                                               placeholder="Orden Visualización"
                                               innerRef={register}
                                               className={(errors.showOrder ? ' is-invalid' : '')}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label>Imagen</Label>
                                        <Input type="text"
                                               name="image"
                                               innerRef={register}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label>Activo</Label>
                                        <br/>
                                        <Switch animate={true} offText={"No"} onText={"Si"}
                                                value={status}
                                                innerRef={register}
                                                onChange={_ => setStatus(!status)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className={'form-container'}>
                        <div className={''}>
                            <h2>{'Ubicación'}</h2>
                        </div>
                        <div className={''}>
                            <Row>
                                <Col md="2">
                                    <FormGroup>
                                        <Label>{'Numero en mapa'}</Label>
                                        <Input type="number" name="number"
                                               placeholder="Ingrese numero"
                                               innerRef={register}
                                               className={(errors.number ? ' is-invalid' : '')}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="8">
                                    <FormGroup>
                                        <Label>Latitud y longitud</Label>
                                        <InputGroup>
                                            <Button color=" background-color-primary" onClick={showMap}>
                                                <i className="fas fa-map-marker-alt text-white"/>
                                            </Button>
                                            <InputGroupAddon addonType="append">
                                                <Input type="number"
                                                       name="lat"
                                                       value={position?.lat}
                                                       className={'lat-lng-input'}
                                                       step={'0.0000001'}
                                                       onChange={e => onLatChange(e.target.value)}
                                                />
                                            </InputGroupAddon>
                                            <InputGroupAddon addonType="append">
                                                <Input type="number"
                                                       name="lng"
                                                       className={'lat-lng-input'}
                                                       value={position?.lng}
                                                       step={'0.0000001'}
                                                       onChange={e => onLngChange(e.target.value)}
                                                />
                                            </InputGroupAddon>
                                        </InputGroup>
                                        <ErrorMessage errors={errors} name="latitude" component="div"
                                                      className="invalid-feedback"/>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row>
                        <Col>
                            {getInternational()}
                        </Col>
                    </Row>
                </Col>
                <Col md={'6'}>
                    {
                        domainObject?.id &&
                        <ActivityScheduleSection path={path} udn={udn} app={app} parent={domainObject}/>
                    }
                </Col>
            </Row>
        </>
    }

    const formFerry = () => {
        return <>
            <Row>
                <Col>
                    <Row>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_CODE}</Label>
                                <Input type="text"
                                       name="code"
                                       placeholder={PH_CODE}
                                       innerRef={register}/>
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_ORDER}</Label>
                                <Input type="number" name="showOrder"
                                       placeholder={PH_ORDER}
                                       innerRef={register}
                                       className={(errors.showOrder ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_IMAGE}</Label>
                                <Input type="text"
                                       name="image"
                                       placeholder={PH_IMAGE}
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{FL_DESTINATION}</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={destinationId}
                                    setExternalValue={setDestinationId}
                                    provider={destinationProvider}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{FL_TYPE_DESTINATION_ACTIVITY}</Label>
                                <br/>
                                <SelectGeneric
                                    initialValue={typeDestinationActivityId}
                                    setExternalValue={setTypeDestinationActivityId}
                                    provider={typeDestinationActivityProvider}
                                    fbDataBaseId={app?.fbDataBase?.id}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_ADITIONAL_COST}</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={aditionalCost}
                                        innerRef={register}
                                        onChange={_ => setAditionalCost(!aditionalCost)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="2">
                            <FormGroup>
                                <Label>{FL_STATUS}</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={status}
                                        innerRef={register}
                                        onChange={_ => setStatus(!status)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                {getInternational()}
            </Row>
        </>
    }

    const formEvents = () => {
        return <>
            <Row>
                <Col md={domainObject?.id !== undefined ? '6' : '12'}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{FL_CODE}</Label>
                                <Input type="text"
                                       name="code"
                                       placeholder="Orden Visualización"
                                       innerRef={register}
                                       className={(errors.showOrder ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{FL_ORDER}</Label>
                                <Input type="number" name="showOrder"
                                       placeholder="Orden Visualización"
                                       innerRef={register}
                                       className={(errors.showOrder ? ' is-invalid' : '')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{FL_STATUS}</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={status}
                                        innerRef={register}
                                        onChange={_ => setStatus(!status)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{'Tipo de audiencia'}</Label>
                                <SelectGeneric
                                    initialValue={typeAudienceId}
                                    setExternalValue={setTypeAudienceId}
                                    provider={typeAudienceProvider}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Categoria</Label>
                                <SelectGeneric
                                    initialValue={categoryId}
                                    setExternalValue={setCategoryId}
                                    provider={categoryProvider}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{FL_ADITIONAL_COST}</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={aditionalCost}
                                        innerRef={register}
                                        onChange={_ => setAditionalCost(!aditionalCost)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label >{'Resaltar'}</Label>
                                <br/>
                                <Switch animate={true} offText={"No"} onText={"Si"}
                                        value={highlight}
                                        innerRef={register}
                                        onChange={_ => setHighlight(!highlight)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label >{'Imagen'}</Label>
                                <Input type="text"
                                       name="image"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="12">
                            <FormGroup>
                                <Label >{'Video url'}</Label>
                                <Input type="text" name="videoUrl"
                                       placeholder="Ingrese url del video"
                                       innerRef={register}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            { getInternational() }
                        </Col>
                    </Row>
                </Col>
                <Col md={'6'}>
                    {
                        domainObject?.id &&
                        <div className={''}>
                            <div className={''}>
                                <h2>{'Imagenes'}</h2>
                            </div>
                            <div className={''}>
                                <ImagePicker
                                    className={'activity'}
                                    title={'Imagenes'}
                                    app={app}
                                    service={activityService}
                                    baseMediaService={activityMediaService}
                                    objectMedia={domainObject}
                                    afterUpload={refreshObject}
                                />
                            </div>
                        </div>
                    }
                    {
                        domainObject?.id && <ActivityItinerarySection path={path } udn={udn} app={app} parent={domainObject}/>
                    }
                </Col>
            </Row>
        </>
    }

    const getInternational = () => {
        return (
            <>
                <div className={'form-container'}>
                    <div className={''}>
                        <h2>{INTERNATIONAL}</h2>
                    </div>
                    <div className={''}>
                        <Row>
                            <ActivityLangs
                                langs={langs}
                                setLangs={setLangs}
                                appCode={app.code}
                                translatableLangs={translatableLangs}
                            />
                        </Row>
                    </div>
                </div>
            </>
        )
    }

    // Render the component.
    return (
        <>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Card className={'card-big-modal'}>
                    <CardTitle className="bg-light border-bottom p-3 mb-0">
                        <>
                            <h2 className={'card-modal-title'} > { (udn?.value || '') + ' / ' + (domainObject?.title || domainObject?.name || '') }</h2>
                            <br />
                            <h5 className={'card-modal-uid'} > { '     uid: ' + (domainObject?.uid || '') }</h5>
                        </>
                        <div className="top-right">
                            <GenericButton
                                style={'table'}
                                icon={'fa-save'}
                                toolTip={TT_SAVE}
                                permission={'add'}
                                type={'submit'}
                            />
                            {
                                editMode && <>
                                    {/*<GenericButton*/}
                                    {/*    style={'table'}*/}
                                    {/*    icon={'fa-copy'}*/}
                                    {/*    toolTip={'Copiar registro'}*/}
                                    {/*    permission={'add'}*/}
                                    {/*    onClick={onCopy}*/}
                                    {/*/>*/}
                                    <GenericButton
                                        style={'table'}
                                        toolTip={TT_HISTORY}
                                        icon={'fa-clock'}
                                        permission={'search'}
                                        onClick={showHistory}
                                    />
                                    <TranslateButton
                                        item={domainObject}
                                        service={eventService}
                                        path={path}
                                    />
                                </>
                            }
                            <Separator/>
                            <GenericButton
                                style={'danger-secondary'}
                                toolTip={TT_CLOSE}
                                icon={'fa-times'}
                                permission={'search'}
                                onClick={closeForm}
                            />
                        </div>
                    </CardTitle>

                    <CardBody className={'card-body-modal'}>
                        {(
                            function() {
                                switch (app.code) {
                                    case "PARK" :
                                        return formPark()
                                    case "FERRY" :
                                        return formFerry()
                                    case "EVENTS" :
                                        return formEvents()
                                    default:
                                        return formAny();
                                }
                        })()}
                    </CardBody>
                </Card>
            </Form>
            {
                showMapModal &&
                <MapPickerModal
                    ref={modalMapRef}
                    setPosition={setPosition}
                    initialPosition={position}
                    googleConfig={mapConfig}
                    updateMarkerPosition={true}
                />
            }

            {
                showHistoryModal && <LogHistoryModal
                    ref={ modalHistoryRef }
                    item={ domainObject }
                />
            }
        </>
    )
}


const mapStateToProps = _ => ({
})

const mapActionsToProps = ({
    setViewMode,
    setItem,
    setForm,
    setLoading
})

export default connect(mapStateToProps, mapActionsToProps)(ActivityForm)